import {
  fetch_nomination,
} from '@/plugins/fetch';

import configuration from '@/configurations';
import {
  getChatbotCustomApiKey,
  loadChatbotSettings,
} from '@/utils/chatbotSettings';

class DifyChatbotRepository {
  get base_url() {
    return `${configuration.api_host}/v1/ai/agentic`.replace(/\/$/, '');
  }

  buildHeaders(extra = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...extra,
    };
    const api_key = getChatbotCustomApiKey(loadChatbotSettings());

    if (api_key) {
      headers['X-Dify-Api-Key'] = api_key;
    }

    return headers;
  }

  buildUrl(path, query = {}) {
    const path_with_base = `${this.base_url}${path}`;
    const url = path_with_base.startsWith('http')
      ? new URL(path_with_base)
      : new URL(path_with_base, window.location.origin);

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  }

  async handleErrorResponse(response) {
    let detail = '';

    try {
      detail = await response.text();
    } catch {
      detail = '';
    }

    const message = detail
      ? `Dify API error ${response.status}: ${detail.slice(0, 200)}`
      : `Dify API error ${response.status}`;

    throw new Error(message);
  }

  async requestJson(path, { method = 'GET', query, body, signal } = {}) {
    const response = await fetch_nomination(this.buildUrl(path, query), {
      method,
      headers: this.buildHeaders(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  /**
   * Send a chat message (streaming SSE).
   * @param {object} input
   * @param {string} input.query
   * @param {string} input.user
   * @param {string} [input.conversation_id]
   * @param {object} [input.inputs]
   * @param {AbortSignal} [input.signal]
   * @returns {Promise<Response>}
   */
  async sendChatMessage({
    query,
    user,
    conversation_id = '',
    inputs = {},
    signal,
  }) {
    const response = await fetch_nomination(this.buildUrl('/chat-messages'), {
      method: 'POST',
      headers: this.buildHeaders({
        Accept: 'text/event-stream',
      }),
      body: JSON.stringify({
        inputs,
        query,
        response_mode: 'streaming',
        conversation_id: conversation_id || '',
        user,
      }),
      signal,
    });

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    return response;
  }

  /**
   * Stop an in-progress generation.
   * @param {object} input
   * @param {string} input.task_id
   * @param {string} input.user
   */
  async stopChatMessage({ task_id, user }) {
    return this.requestJson(`/chat-messages/${task_id}/stop`, {
      method: 'POST',
      body: { user },
    });
  }

  /**
   * @param {object} input
   * @param {string} input.user
   * @param {string} [input.last_id]
   * @param {number} [input.limit]
   */
  async getConversations({ user, last_id, limit = 50 }) {
    return this.requestJson('/conversations', {
      query: {
        user,
        last_id,
        limit,
        sort_by: '-updated_at',
      },
    });
  }

  /**
   * @param {object} input
   * @param {string} input.conversation_id
   * @param {string} input.user
   * @param {string} [input.first_id]
   * @param {number} [input.limit]
   */
  async getMessages({
    conversation_id,
    user,
    first_id,
    limit = 50,
  }) {
    return this.requestJson('/messages', {
      query: {
        conversation_id,
        user,
        first_id,
        limit,
      },
    });
  }

  /**
   * @param {object} input
   * @param {string} input.user
   */
  async getParameters({ user }) {
    return this.requestJson('/parameters', {
      query: { user },
    });
  }

  /**
   * Follow-up questions suggested after a bot answer.
   * Requires suggested_questions_after_answer to be enabled on the app.
   * @param {object} input
   * @param {string} input.message_id
   * @param {string} input.user
   * @returns {Promise<{ result: string, data: string[] }>}
   */
  async getSuggestedQuestions({ message_id, user }) {
    return this.requestJson(`/messages/${message_id}/suggested`, {
      query: { user },
    });
  }

  /**
   * Basic app metadata (name, description, mode, …).
   * @returns {Promise<object>}
   */
  async getInfo() {
    return this.requestJson('/info');
  }

  /**
   * WebApp settings (branding, show_workflow_steps, …).
   * @returns {Promise<object>}
   */
  async getSite() {
    return this.requestJson('/site');
  }

  /**
   * @param {object} input
   * @param {string} input.conversation_id
   * @param {string} input.user
   */
  async deleteConversation({ conversation_id, user }) {
    return this.requestJson(`/conversations/${conversation_id}`, {
      method: 'DELETE',
      body: { user },
    });
  }

  /**
   * List conversation variables (optionally filtered by name).
   * @param {object} input
   * @param {string} input.conversation_id
   * @param {string} input.user
   * @param {string} [input.variable_name]
   * @param {number} [input.limit]
   */
  async getConversationVariables({
    conversation_id,
    user,
  }) {
    return this.requestJson(`/conversations/${conversation_id}/variables`, {
      query: {
        user,
      },
    });
  }

  /**
   * Update a conversation variable value.
   * @param {object} input
   * @param {string} input.conversation_id
   * @param {string} input.variable_id
   * @param {string} input.user
   * @param {*} input.value
   */
  async updateConversationVariable({
    conversation_id,
    variable_id,
    user,
    value,
  }) {
    return this.requestJson(
      `/conversations/${conversation_id}/variables/${variable_id}`,
      {
        method: 'PUT',
        body: {
          value,
          user,
        },
      }
    );
  }
}

const dify_chatbot_repository = new DifyChatbotRepository();

export {
  dify_chatbot_repository,
};
